import { from, EMPTY, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

let CLIENT_ID =
    typeof OIDC_CLIENT_ID === 'string'
        ? OIDC_CLIENT_ID
        : 'aN1mRpxnVG04pkU2mF3b61SIBTRNlhO-';
let ISSUER =
    typeof OIDC_ISSUER === 'string' ? OIDC_ISSUER : 'https://auth.ma300k.de';
let TOKEN_KEY = 'm3k-learn-session-token';

export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
    localStorage.removeItem(TOKEN_KEY);
}

export function redirectToAuth() {
    let redirectUri = `${window.location.protocol}//${window.location.host}/auth-grant.html`;
    let url = `${ISSUER}/authorize?response_type=code&client_id=${CLIENT_ID}&scope=openid&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = url;
}

/**
 * Injects the Bearer token from localStorage into every request.
 * If no token is present, redirects to the auth page immediately.
 */
export function authorizedFetch(path, opts = {}) {
    let token = getToken();
    if (!token) {
        redirectToAuth();
        return Promise.reject(new Error('Unauthenticated'));
    }
    return fetch(path, {
        ...opts,
        headers: {
            ...opts.headers,
            Authorization: `Bearer ${token}`,
        },
    });
}

/**
 * Pipeable operator that normalises HTTP error handling:
 *   - 401 → clear token, redirect to auth, complete silently
 *   - non-OK → throw the Response as an error
 *   - OK → pass through unchanged
 */
export function validateResponse() {
    return switchMap((response) => {
        if (response.status === 401) {
            clearToken();
            redirectToAuth();
            return EMPTY;
        }
        if (!response.ok) {
            throw response;
        }
        return [response];
    });
}

export function fetchJson(path, opts = {}) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(path, opts))),
        validateResponse(),
        switchMap((response) => from(response.json()))
    );
}

export function postJson(path, body, opts = {}) {
    return of(undefined).pipe(
        switchMap(() =>
            from(
                authorizedFetch(path, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                    ...opts,
                })
            )
        ),
        validateResponse(),
        switchMap((response) => from(response.json()))
    );
}

export function putJson(path, body, opts = {}) {
    return of(undefined).pipe(
        switchMap(() =>
            from(
                authorizedFetch(path, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                    ...opts,
                })
            )
        ),
        validateResponse(),
        switchMap((response) => from(response.json()))
    );
}

export function deleteEntity(path, opts = {}) {
    return of(undefined).pipe(
        switchMap(() =>
            from(
                authorizedFetch(path, {
                    method: 'DELETE',
                    ...opts,
                })
            )
        ),
        validateResponse(),
        switchMap(() => EMPTY)
    );
}
